import { BaseWebComponentView } from '../../../framework/BaseWebComponentView';
import { Column, ColumnKeys, Dependencies } from '../types';
import {
  WEB_COMPONENT_GRID_VIEW,
  WEB_COMPONENT_GZ_SELECT,
  WEB_COMPONENT_GZ_TITLE,
} from 'apps/webcomponents/src/webcomponents.constants';
import { GridViewWebComponent } from 'apps/webcomponents/src/grid/gridView';
import { ExchangeProtectionGridDataProvider } from '../dataProvider/ExchangeProtectionGridDataProvider';
import { GzSelectWebComponent } from 'apps/webcomponents/src/ui/gzSelect';
import { SelectOption } from 'apps/webcomponents/src/ui/gzSelect/types';
import { GridConfig } from './GridConfig';
import { GridViewMetadata, ViewConfiguration, ViewType } from '../../../grid/gridView/types';
import { SelectableAction } from '../../computersAndVirtualMachines/types';

export class ExchangeProtectionView extends BaseWebComponentView {
  public dom: {
    $grid: GridViewWebComponent<Column, ColumnKeys>;
    $select: GzSelectWebComponent;
    $wrapper: HTMLElement;
    $save: HTMLElement;
    $actionsBtn: HTMLElement;
    $emptyQuarantineBtn: HTMLElement;
    $deleteTaskBtn: HTMLElement;
    $hostElement: HTMLElement;
    $title: HTMLElement;
  };

  public dependencies: Dependencies;

  private selectOptions: Array<SelectOption>;

  getDomElements() {
    this.dom.$grid = this.shadow.querySelector(WEB_COMPONENT_GRID_VIEW);
    this.dom.$select = this.shadow.querySelector(WEB_COMPONENT_GZ_SELECT);
    this.dom.$title = this.shadow.querySelector(WEB_COMPONENT_GZ_TITLE);
    this.dom.$wrapper = this.shadow.querySelector('[data-wrapper]');
    this.dom.$save = this.shadow.querySelector('[data-save]');
    this.dom.$actionsBtn = this.shadow.querySelector('[data-actions]');
    this.dom.$emptyQuarantineBtn = this.shadow.querySelector('#empty-quarantine');
    this.dom.$deleteTaskBtn = this.shadow.querySelector('#delete-task');
    this.dom.$hostElement = this.shadow;
  }

  get allTranslations() {
    return this.translations;
  }

  public buildView(data): void {
    const { viewMetaData, actions } = new GridConfig().getDataForView(
      this.dependencies,
      data?.response?.companyFilters
    );

    this.buildGrid(viewMetaData);
    this.setActions(actions);
    this.setTranslations();
  }

  public enableSaveExchangeProtection(enabled: boolean): void {
    this.enableDisableElement(this.dom.$save, enabled);
  }

  public enableRestore(enabled: boolean): void {
    this.enableDisableSelectItem(enabled, SelectableAction.RESTORE);
    this.enableDisableSelectItem(enabled, SelectableAction.RESTORE_ORIGINAL);
  }

  public enableDelete(enabled: boolean): void {
    this.enableDisableElement(this.dom.$deleteTaskBtn, enabled);
  }
  private enableDisableSelectItem(enabled: boolean, selectableAction: SelectableAction) {
    this.selectOptions = this.selectOptions.map(option => ({
      ...option,
      disabled: option.value === selectableAction ? !enabled : option.disabled,
    }));

    this.dom.$select.options = this.selectOptions;
    this.enableDisableElement(this.dom.$deleteTaskBtn, enabled);
  }

  public enableEmptyQuarantine(enabled: boolean) {
    this.enableDisableElement(this.dom.$emptyQuarantineBtn, enabled);
  }

  private enableDisableElement(element, enabled: boolean): void {
    const enabledToMethod = {
      true: 'removeAttribute',
      false: 'setAttribute',
    };

    element[enabledToMethod[String(enabled)]]('disabled', '');
  }

  private buildGrid(viewMetaData: GridViewMetadata<Column, ColumnKeys>) {
    const grid = this.dom.$grid;
    const defaultViewProps: ViewConfiguration<Column, ColumnKeys> = {
      compactView: viewMetaData.isCompactViewChecked,
      displayedColumns: viewMetaData.columns,
      filters: viewMetaData.filters,
      name: '',
      pageSize: viewMetaData.pageSize,
      type: ViewType.DEFAULT,
      id: viewMetaData.viewId,
    };

    grid.propertiesSet = false;
    grid.dataProvider = new ExchangeProtectionGridDataProvider(this.dependencies);
    grid.enableRowClickEvent = true;
    grid.sideMenuParent = this.dom.$hostElement;

    for (const [key, value] of Object.entries(viewMetaData)) {
      grid[key] = value;
    }
    grid.defaultViewProps = defaultViewProps;
    grid.propertiesSet = true;
    grid.injectDependencies(this.dependencies);
    grid.start().then().catch();
  }

  private setActions(actions) {
    this.selectOptions = actions;
    this.dom.$select.options = this.selectOptions;
  }

  private setTranslations(): void {
    this.dom.$save.textContent = this.dependencies.translateFn('Save#preserve state');
    this.dom.$actionsBtn.textContent = this.dependencies.translateFn('Restore');
    this.dom.$emptyQuarantineBtn.textContent = this.dependencies.translateFn('Empty Quarantine');
    this.dom.$deleteTaskBtn.textContent = this.dependencies.translateFn('Delete');
    this.dom.$title.textContent = this.dependencies.translateFn('Exchange servers');
  }
}
